import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Inbox } from "lucide-react";
import {
  fileToDataUrl,
  getCirculars as getLocalCirculars,
  getSessionUser,
  saveCircularForAllClasses,
} from "@/lib/eisStore";
import {
  getCirculars as getCircularsFromSupabase,
  isSupabaseConfigured,
  uploadCircular,
} from "@/lib/supabaseOps";

function CircularList({ items }) {
  return (
    <div className="space-y-3">
      {items.length ? (
        items.map((item) => (
          <div key={item.id} className="rounded-xl border border-slate-200 p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-800">{item.title}</p>
                <p className="text-xs text-slate-500">
                  {new Date(item.createdAt).toLocaleString()}
                </p>
              </div>
              <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                All classes
              </span>
            </div>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{item.body}</p>
            {item.fileUrl && (
              <a
                href={item.fileUrl}
                download={item.fileName || "circular.pdf"}
                className="inline-flex rounded-full px-4 py-2 text-white font-semibold"
                style={{ backgroundColor: "#0b1f7a" }}
              >
                Open PDF
              </a>
            )}
          </div>
        ))
      ) : (
        <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
          <div
            className="h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: "#EEF0FA" }}
          >
            <Inbox size={28} style={{ color: "#0b1f7a" }} />
          </div>
          <p data-testid="circulars-empty" className="text-lg font-semibold text-slate-700">
            no circulars yet
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Circulars from the school will appear here.
          </p>
        </div>
      )}
    </div>
  );
}

function AdminCirculars() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [circulars, setCirculars] = useState(getLocalCirculars());

  useEffect(() => {
    let cancelled = false;
    const loadCirculars = async () => {
      if (!isSupabaseConfigured) return;
      try {
        const rows = await getCircularsFromSupabase();
        if (!cancelled && rows?.length) {
          setCirculars(rows.map((row) => ({
            id: row.id,
            title: row.title,
            body: row.body || row.description || "",
            fileUrl: row.file_url || row.file_path || "",
            fileName: row.file_name || row.title || "circular.pdf",
            createdAt: row.created_at || row.inserted_at || new Date().toISOString(),
          })));
        }
      } catch (error) {
        console.error(error);
      }
    };
    loadCirculars();
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setFileUrl(url);
    setFileName(file.name);
    setSelectedFile(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a circular title");
      return;
    }
    try {
      if (isSupabaseConfigured && selectedFile) {
        await uploadCircular(selectedFile, title.trim());
      }
      saveCircularForAllClasses({
        title: title.trim(),
        body: body.trim(),
        fileUrl,
        fileName,
      });
      setCirculars((current) => [
        {
          id: crypto.randomUUID(),
          title: title.trim(),
          body: body.trim(),
          fileUrl,
          fileName,
          createdAt: new Date().toISOString(),
        },
        ...current,
      ]);
      setTitle("");
      setBody("");
      setFileUrl("");
      setFileName("");
      setSelectedFile(null);
      toast.success("Circular published");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Unable to publish circular");
    }
  };

  return (
    <AppShell>
      <div data-testid="circulars-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Circulars
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Upload Circular</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="mt-1 min-h-32" />
                </div>
                <div>
                  <Label>PDF</Label>
                  <Input
                    type="file"
                    accept="application/pdf"
                    className="mt-1"
                    onChange={(e) => uploadFile(e.target.files?.[0])}
                  />
                  {fileName && <p className="text-xs text-slate-500 mt-1">{fileName}</p>}
                </div>
                <Button
                  type="submit"
                  className="rounded-full text-white"
                  style={{ backgroundColor: "#0b1f7a" }}
                >
                  Publish Circular
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Published Circulars</CardTitle>
            </CardHeader>
            <CardContent>
              <CircularList items={circulars} />
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

function StudentCirculars() {
  const [circulars, setCirculars] = useState(getLocalCirculars());

  useEffect(() => {
    let cancelled = false;
    const loadCirculars = async () => {
      if (!isSupabaseConfigured) return;
      try {
        const rows = await getCircularsFromSupabase();
        if (cancelled) return;
        setCirculars(
          rows.map((row) => ({
            id: row.id,
            title: row.title,
            body: row.body || row.description || "",
            fileUrl: row.file_url || row.file_path || "",
            fileName: row.file_name || row.title || "circular.pdf",
            createdAt: row.created_at || row.inserted_at || new Date().toISOString(),
          })),
        );
      } catch (error) {
        console.error(error);
      }
    };
    loadCirculars();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell>
      <div data-testid="circulars-page" className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
          Circulars
        </h2>
        <Card>
          <CardContent className="p-6">
            <CircularList items={circulars} />
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

export default function Circulars() {
  const user = getSessionUser();
  if (user?.role === "admin") {
    return <AdminCirculars />;
  }
  return <StudentCirculars />;
}
